<script setup lang="ts">
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import { SortItem } from "vuetify/lib/components/VDataTable/composables/sort";
import { useApiClient } from "@/api/client";
import { onMounted, ref, computed, watch, toRefs } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { useDashboardStore } from "@/modules/dashboards/store";
import { t } from "@/plugins/i18n";
import { formatNumber, processedDocument } from "chartjs-graph";
import { useAbortController } from "@/modules/explorer/composables/useAbortController";
import { useCloned } from "@vueuse/core/index";
import {
	BUDGET_COMPARISON_LABEL,
	getComparisonCellState,
	getUnavailableComparisonLabels,
	hasDocumentChanged,
} from "@/modules/explorer/components/QuickFilter/quickFilterTools";
import QuickFilterComparisonValueCell from "@/modules/explorer/components/QuickFilter/QuickFilterComparisonValueCell.vue";
import CustomTableHeader from "@/modules/explorer/components/QuickFilter/CustomTableHeader.vue";

/**
 * The default version of the Quick Filter Menu (QFM).
 *
 * Used for anything that isn't a hierarchy (buckets, nominal dimensions)
 * This version is much simpler than the hierarchical QFM, as it needs to do a whole lot less
 */

const explorerClient = useApiClient("explorer");
const explorerStore = useExplorerStore();
const dashboardStore = useDashboardStore();
const { abortController, abortRequestAndResetController } =
	useAbortController();

export type SimpleDimensionValue = {
	id: number;
	label: string;
};

type SimpleDimensionValueWithValue = SimpleDimensionValue & {
	value: number | null;
	comparisonValues: Record<string, number>;
};

const props = defineProps<{
	activeFilter: ActiveFilterDTO;
	isExplorer?: boolean;
}>();
const { activeFilter, isExplorer } = toRefs(props);

const emit = defineEmits<{
	(e: "resize", height: number): void;
}>();

const filterValues = ref<SimpleDimensionValueWithValue[]>([]);
const selectedValues = ref<Set<number>>(new Set());
const hoveredRow = ref<number | null>(null);
const isLoading = ref(true);
// Whether the currently-hovered row's label is actually ellipsis-truncated — CSS can't expose
// this, so it's measured on hover and used to decide whether to show a tooltip with the full text.
const isLabelTruncated = ref(false);

const checkLabelTruncation = (event: MouseEvent) => {
	const target = event.currentTarget as HTMLElement;
	isLabelTruncated.value = target.scrollWidth > target.clientWidth;
};
// True while values are being silently refetched because ANOTHER filter/quick filter changed
// something (not the initial load) — surfaced as a subtle animated indicator on the title.
const isSilentlyUpdating = ref(false);

// Tracks which comparison labels have been included in a completed fetch, so a newly
// added comparison period (e.g. a new YoY year) shows a loading state instead of a
// misleading 0 until its data actually arrives.
const loadedComparisonLabels = ref<Set<string>>(new Set());
// Budget comparison specifically, when it's configured on the document but the backend never
// returned it for any row of the last completed fetch (it isn't supported for this dimension) —
// rendered as "n/a". Period comparisons are always computable, so they render as 0 instead.
const unavailableComparisonLabels = ref<Set<string>>(new Set());

const { cloned: previousDocument, sync: syncDocument } = useCloned(
	explorerStore.document,
	{
		manual: true,
	}
);

const isAllValuesZero = (v: SimpleDimensionValueWithValue) =>
	v.value !== null &&
	!v.value &&
	Object.values(v.comparisonValues ?? {}).every((value) => !value);

const visibleValues = computed(() => {
	const areValuesLoading = filterValues.value.some((v) => v.value === null);
	const visibleValues = filterValues.value.filter((v) => !isAllValuesZero(v));

	return areValuesLoading ? visibleValues.slice(0, 50) : visibleValues;
});

// Mirrors DocumentDateComparisonDTO::getComparisonPeriodLabels() on the backend, minus the primary
// label. Ordered oldest-to-newest (Y-7, ..., Y-1), then custom periods, then budget, so it reads
// left-to-right the same way it would chronologically, ending right before the current-year column.
//
// Dashboards (isExplorer false) have no comparison concept at all — DashboardDO has no
// date_comparison field — and explorerStore.document is null outside of Explorer, so this must
// bail out before touching it. This computed is read unconditionally by template `v-for`s (slot
// definitions evaluate regardless of whether those columns end up rendered), so it has to be the
// one place that's safe, rather than relying on every call site to guard itself.
const comparisonLabels = computed(() => {
	if (!isExplorer.value || !explorerStore.document) return [];

	const dateComparison = explorerStore.document.date_comparison;

	return [
		...[...dateComparison.periods]
			.sort((a, b) => a - b)
			.map((period) => `Y${period}`),
		...dateComparison.customPeriods.map((period) => period.name),
		dateComparison.isBudgetComparisonActive ? BUDGET_COMPARISON_LABEL : null,
	].filter((label): label is string => !!label);
});

// Each numeric column gets a fixed pixel width (below); the label column is left without a
// declared width, so under table-layout: fixed it simply absorbs whatever space is left over —
// staying spacious/right-aligned as today when there's room. minTableWidth is a floor equal to
// that same fixed sum, so the table only ever grows (and its wrapper scrolls horizontally)
// once there genuinely isn't enough room, instead of squeezing numeric columns unreadably thin.
//
// All of this (fixed-width value column, truncated metric-name title, table-width floor) only
// applies once comparison is actually active — with no comparison, the value column is the only
// numeric column and should just show the full metric name, same as before this feature existed.
const LABEL_COLUMN_MIN_WIDTH = 120;
const NUMERIC_COLUMN_WIDTH = 100;

const hasActiveComparison = computed(() => comparisonLabels.value.length > 0);

const minTableWidth = computed(() => {
	if (!isExplorer.value || !hasActiveComparison.value) return 0;

	const numericColumnCount = comparisonLabels.value.length + 1;

	return LABEL_COLUMN_MIN_WIDTH + numericColumnCount * NUMERIC_COLUMN_WIDTH;
});

const vertical = computed(() => {
	// Currently only ever evaluated when isExplorer is true (its consumers are all gated on that
	// already), but guard explicitly rather than relying on that staying true everywhere.
	if (!isExplorer.value || !explorerStore.document) return undefined;

	if (explorerStore.document.view_tab === "graph") {
		return explorerStore.document?.vertical;
	} else {
		return explorerStore.document?.data_tables?.[0]?.metrics?.[0];
	}
});

const headers = computed(() => [
	{
		title: t(`properties.${activeFilter.value.dimension.name_clean}.title`),
		key: "label",
	},
	...(isExplorer.value
		? comparisonLabels.value.map((label) => ({
				title: label,
				align: "end" as const,
				key: `comparison_${label}`,
				width: `${NUMERIC_COLUMN_WIDTH}px`,
				// The value lives at item.comparisonValues[label], not at a flat item[key], so
				// Vuetify's default getObjectValueByPath(item, key) sort can't find it — sortRaw
				// receives the full raw item instead, letting us read the nested value ourselves.
				sortRaw: (
					a: SimpleDimensionValueWithValue,
					b: SimpleDimensionValueWithValue
				) =>
					(a.comparisonValues?.[label] ?? 0) -
					(b.comparisonValues?.[label] ?? 0),
		  }))
		: []),
	isExplorer.value && {
		title: t(`properties.${vertical.value?.name_clean}.title`),
		align: "end",
		key: "value",
		...(hasActiveComparison.value ? { width: `${NUMERIC_COLUMN_WIDTH}px` } : {}),
	},
]);

const getDimensionValues = async () => {
	const MAX_CHUNK_SIZE = 10000;

	filterValues.value = [];

	while (true) {
		const response =
			activeFilter.value.dimension.type === "bucketed"
				? await explorerClient.getBucketValuesForQuickFilter(
						activeFilter.value.dimension.dimension_bucket_id,
						filterValues.value.length,
						MAX_CHUNK_SIZE
				  )
				: await explorerClient.getDimensionValuesForQuickFilter(
						activeFilter.value.dimension.id,
						filterValues.value.length,
						MAX_CHUNK_SIZE
				  );

		if (response.isOk()) {
			const toAppend = response.value.map((value) => ({
				...value,
				value: null,
				comparisonValues: {},
			}));

			filterValues.value.push(...toAppend);
			if (toAppend.length !== MAX_CHUNK_SIZE) break;
		} else {
			break;
		}
	}
	isLoading.value = false;
	await getDimensionValueValues();
};

const getDimensionValueValues = async () => {
	if (!isExplorer.value) return;

	abortRequestAndResetController();
	const response = await explorerClient.getFilterValues(
		processedDocument(explorerStore.document),
		activeFilter.value.dimension,
		abortController.value?.signal ?? null
	);
	if (response.isOk()) {
		const map = response.value;

		filterValues.value = filterValues.value.map((value) => ({
			...value,
			value: map[value.label]?.totalValue ?? 0,
			comparisonValues: map[value.label]?.comparisonValues ?? {},
		}));

		// An empty map means nothing was fetched (e.g. no dimension values yet), not that every
		// comparison is unavailable, so only judge availability once there's something to judge against.
		if (Object.keys(map).length > 0) {
			unavailableComparisonLabels.value = getUnavailableComparisonLabels(
				comparisonLabels.value,
				map
			);
		}
		loadedComparisonLabels.value = new Set(comparisonLabels.value);
	}
};

const applyValues = () => {
	const newFilter = {
		...activeFilter.value,
		values: filterValues.value.filter((v) => selectedValues.value.has(v.id)),
	};

	if (JSON.stringify(newFilter) === JSON.stringify(activeFilter.value)) {
		return;
	}

	if (isExplorer.value) {
		explorerStore.APPLY_FILTER(newFilter, "edit");
	} else {
		dashboardStore.applyFilter(newFilter, "edit");
	}
};

// Persists the QFM's sort column/direction onto the filter (mirrors HierarchyQuickFilter.vue),
// so it's stored on the document and reapplied when the configuration is loaded again.
const sortBy = computed({
	get: (): SortItem[] => [
		{
			key: activeFilter.value?.sortBy ?? "",
			order: activeFilter.value?.sortDirection || false,
		},
	],
	set: (val: SortItem[]) => {
		const [sortBy, sortDirection] = [
			val[0]?.key ?? null,
			typeof val[0]?.order === "boolean" ? null : val[0]?.order,
		];
		const newFilter = { ...activeFilter.value, sortBy, sortDirection };

		if (JSON.stringify(newFilter) === JSON.stringify(activeFilter.value)) {
			return;
		}

		if (isExplorer.value) {
			explorerStore.APPLY_FILTER(newFilter, "edit");
		} else {
			dashboardStore.applyFilter(newFilter, "edit");
		}
	},
});

const toggleSelection = (id: number) => {
	const filterValuesWithData = filterValues.value.filter((v) => v.value);

	if (selectedValues.value.size === 0) {
		filterValuesWithData
			.filter((v) => v.id !== id)
			.forEach((v) => selectedValues.value.add(v.id));

		return;
	}

	if (selectedValues.value.has(id)) {
		selectedValues.value.delete(id);
	} else {
		selectedValues.value.add(id);
	}

	if (
		filterValuesWithData.every((v) => selectedValues.value.has(v.id)) &&
		filterValues.value.length > 250
	) {
		selectedValues.value = new Set();
	}
};

const isOnly = (id: number) =>
	selectedValues.value.size === 1 && Array.from(selectedValues.value)[0] === id;

const selectOnly = (id: number) => {
	selectedValues.value = new Set([id]);
};

const selectAll = () => {
	selectedValues.value = activeFilter.value.valuesAreIncluded
		? new Set([])
		: new Set(visibleValues.value.map((v) => v.id));
};

const isItemIncluded = (itemId: number) => {
	if (selectedValues.value.size === 0) {
		return activeFilter.value.valuesAreIncluded;
	}

	return (
		selectedValues.value.has(itemId) === activeFilter.value.valuesAreIncluded
	);
};

watch(
	() => activeFilter.value.values,
	() => {
		selectedValues.value = new Set(activeFilter.value.values.map((v) => v.id));
	},
	{ deep: true, immediate: true }
);

watch(selectedValues, applyValues, { deep: true });

onMounted(getDimensionValues);

watch(
	() => explorerStore.explorerState.lastUpdated.getTime(),
	async () => {
		// explorerStore.document is null outside of Explorer (dashboards have no equivalent), so
		// there's nothing to diff against — and no silent-refetch signal is meaningful there anyway,
		// since getDimensionValueValues() already no-ops when !isExplorer.
		if (
			isExplorer.value &&
			explorerStore.document &&
			hasDocumentChanged(
				explorerStore.document,
				previousDocument.value,
				activeFilter.value.dimension.id
			)
		) {
			isSilentlyUpdating.value = !isLoading.value;
			await getDimensionValueValues();
			isSilentlyUpdating.value = false;
			syncDocument();
		}
	},
	{ immediate: true }
);

watch(visibleValues, () => {
	if (
		visibleValues.value.length &&
		!visibleValues.value.some((child) => child.value === null)
	) {
		emit("resize", visibleValues.value?.length);
	}
});

const rowProps = (item) => ({
	class: [`row ${isItemIncluded(item.id) ? "" : "excluded-branch"}`],
	onMouseover: () => (hoveredRow.value = item.id),
	onMouseleave: () => (hoveredRow.value = null),
});
</script>

<template>
	<div
		class="w-100 px-2 overflow-y-scroll overflow-x-auto"
		:style="{ '--min-table-width': `${minTableWidth}px` }"
	>
		<v-data-table
			v-model:sort-by="sortBy"
			:headers="headers"
			:loading="isLoading"
			:items="visibleValues"
			:items-per-page="-1"
			density="compact"
			class="w-100 table"
			hide-default-footer
		>
			<template v-slot:header.label="{ column }">
				<custom-table-header header-key="label" :sort-by="sortBy">
					<span
						v-tooltip="column.title"
						class="text-truncate"
						style="min-width: 0"
						>{{ column.title }}</span
					>
					<v-fade-transition>
						<v-progress-linear
							v-if="isSilentlyUpdating"
							indeterminate
							height="3"
							style="width: 100px"
							bg-color="transparent"
							class="mx-5"
						/>
					</v-fade-transition>
				</custom-table-header>
			</template>

			<template v-slot:header.value="{ column }">
				<custom-table-header header-key="value" :sort-by="sortBy" align="end">
					<span
						v-tooltip="hasActiveComparison ? column.title : false"
						:class="{ 'text-truncate': hasActiveComparison }"
						:style="hasActiveComparison ? 'min-width: 3ch' : ''"
						>{{ column.title }}</span
					>
				</custom-table-header>
			</template>

			<template
				v-for="label in comparisonLabels"
				:key="label"
				#[`header.comparison_${label}`]="{ column }"
			>
				<custom-table-header :header-key="`comparison_${label}`" :sort-by="sortBy" align="end">
					<span v-tooltip="column.title" class="text-truncate" style="max-width: 4ch"
						>{{ column.title }}</span
					>
				</custom-table-header>
			</template>

			<template #item.label="{ item }">
				<div v-bind="rowProps(item)" class="ga-4">
					<div
						class="d-flex align-center pl-1"
						:class="{ 'excluded-branch': !isItemIncluded(item.id) }"
						style="min-width: 0"
					>
						<v-checkbox-btn
							:model-value="isItemIncluded(item.id)"
							:disabled="isItemIncluded(item.id) && selectedValues.size === 1"
							density="comfortable"
							@click="toggleSelection(item.id)"
						/>
						<span
						v-tooltip="isLabelTruncated ? item.label : false"
						class="text-truncate"
						style="min-width: 0"
						@mouseenter="checkLabelTruncation"
						>{{ item.label }}</span
					>
					</div>
					<v-fade-transition>
						<div
							v-if="hoveredRow === item.id && filterValues.length > 1"
							class="flex-shrink-0"
						>
							<v-btn
								v-if="isOnly(item.id)"
								size="x-small"
								height="24"
								width="48"
								variant="text"
								class="only-all"
								@click.stop="selectAll"
							>
								All
							</v-btn>
							<v-btn
								v-else
								size="x-small"
								height="24"
								width="48"
								variant="text"
								class="only-all"
								@click.stop="selectOnly(item.id)"
							>
								Only
							</v-btn>
						</div>
					</v-fade-transition>
				</div>
			</template>

			<template #item.value="{ item }">
				<div v-bind="rowProps(item)" class="pr-4 justify-end">
					<v-progress-linear
						v-if="item?.value === null"
						height="3"
						style="width: 30px"
						bg-color="transparent"
						indeterminate
					/>
					<template v-else>
						{{ formatNumber(item.value, vertical?.format) }}
					</template>
				</div>
			</template>

			<template
				v-for="label in comparisonLabels"
				:key="label"
				#[`item.comparison_${label}`]="{ item }"
			>
				<div v-bind="rowProps(item)" class="pr-4 justify-end">
					<QuickFilterComparisonValueCell
						:state="
							getComparisonCellState(
								item,
								label,
								loadedComparisonLabels,
								unavailableComparisonLabels
							)
						"
						:format="vertical?.format"
					/>
				</div>
			</template>
		</v-data-table>
	</div>
</template>

<style scoped lang="scss">
.table :deep(table) {
	table-layout: fixed;
	width: 100%;
	min-width: var(--min-table-width, 0);
}

.table {
	font-size: 13px;
}

.row {
	display: flex;
	align-items: center;
	width: calc(100% + 2rem);
	height: 100%;
	margin: 0 -1rem;
	user-select: none;
}

.excluded-branch {
	color: rgba(var(--v-theme-on-surface), 0.5);
}

.only-all {
	color: rgb(var(--v-theme-on-surface));
	text-transform: capitalize;
	font-weight: 400;
	font-size: 12px;
}
</style>
